import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { Corridor, NetworkLocation } from '../types';

/** Dani nedelje kao zbir, isto kao u bazi: pon 1, uto 2, sre 4… */
const DAYS: { flag: number; label: string }[] = [
  { flag: 1, label: 'pon' },
  { flag: 2, label: 'uto' },
  { flag: 4, label: 'sre' },
  { flag: 8, label: 'čet' },
  { flag: 16, label: 'pet' },
  { flag: 32, label: 'sub' }
];

interface Props {
  corridors: Corridor[];
  onChanged: () => void;
}

/**
 * Podešavanje pravaca: naziv, nedeljni raspored i koje objekte pravac pokriva.
 *
 * <b>Raspored je podsetnik, ne raspoređivač.</b> Kvačica govori kojim danima se
 * taj pravac vozi, i po tome se planira — ali nijedna tura ne nastaje sama.
 * Operater i dalje bira šta ide u vozilo.
 *
 * <b>Stanice i put su dve različite stvari.</b> Stanica je objekat kome se nosi
 * roba; put je niz mesta kroz koja se vozi, i on ide kroz Lugovo i Stapar gde
 * nema nijednog objekta. Zato se menjaju odvojeno: premeštanje B. Palanke sa
 * jednog pravca na drugi ne znači da se i put promenio.
 */
export default function CorridorSetup({ corridors, onChanged }: Props) {
  const [locations, setLocations] = useState<NetworkLocation[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [flags, setFlags] = useState(0);
  const [stops, setStops] = useState<{ kind: string; code: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.locations().then(setLocations).catch(() => setLocations([]));
  }, []);

  const start = useCallback((corridor: Corridor) => {
    setEditing(corridor.id);
    setName(corridor.name);
    setFlags(corridor.dayFlags);
    setStops(corridor.stops.map((s) => ({ kind: s.kind, code: s.code })));
    setError(null);
  }, []);

  const nameOf = (kind: string, code: string) =>
    locations.find((l) => l.kind === kind && l.code === code)?.name ?? `${kind} ${code}`;

  const save = async () => {
    if (editing === null) return;

    setBusy(true);
    try {
      await api.updateCorridor(editing, name.trim(), flags, true);
      await api.setCorridorStops(editing, stops);
      setEditing(null);
      onChanged();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Brzo prebacivanje dana bez ulaska u izmenu — raspored se najčešće samo štiklira. */
  const toggleDay = async (corridor: Corridor, flag: number) => {
    setBusy(true);
    try {
      await api.updateCorridor(
        corridor.id, corridor.name, corridor.dayFlags ^ flag, corridor.isActive);
      onChanged();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const free = locations.filter(
    (l) => l.kind === 'Store' && !stops.some((s) => s.code === l.code && s.kind === l.kind));

  return (
    <section className="panel">
      <h2>Nedeljni raspored</h2>

      <p className="note">
        Kvačica kaže kojim danima se pravac vozi. To je podsetnik po kom se
        planira — nijedna tura ne nastaje sama.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <table className="grid week">
        <thead>
          <tr>
            <th>Pravac</th>
            {DAYS.map((day) => <th key={day.flag} className="num">{day.label}</th>)}
            <th>Objekti</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {corridors.map((corridor) => (
            <tr key={corridor.id}>
              <td className="week__name">{corridor.name}</td>

              {DAYS.map((day) => (
                <td key={day.flag} className="num">
                  <button
                    type="button"
                    className={`week__day${(corridor.dayFlags & day.flag) !== 0 ? ' week__day--on' : ''}`}
                    disabled={busy}
                    onClick={() => void toggleDay(corridor, day.flag)}
                    aria-pressed={(corridor.dayFlags & day.flag) !== 0}
                  >
                    {(corridor.dayFlags & day.flag) !== 0 ? '✓' : ''}
                  </button>
                </td>
              ))}

              <td className="muted">
                {corridor.stops.length === 0
                  ? 'nijedan'
                  : corridor.stops.map((s) => nameOf(s.kind, s.code)).join(', ')}
              </td>

              <td>
                <button type="button" className="link" onClick={() => start(corridor)}>
                  uredi
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing !== null && (
        <div className="setup">
          <h3>Izmena pravca</h3>

          <label>
            Naziv
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <div className="setup__days">
            {DAYS.map((day) => (
              <label key={day.flag} className="setup__day">
                <input
                  type="checkbox"
                  checked={(flags & day.flag) !== 0}
                  onChange={() => setFlags(flags ^ day.flag)}
                />
                {day.label}
              </label>
            ))}
          </div>

          <p className="setup__title">Objekti na pravcu, redom</p>

          <ol className="setup__stops">
            {stops.map((stop, index) => (
              <li key={`${stop.kind}-${stop.code}`}>
                {nameOf(stop.kind, stop.code)}
                <span className="setup__stopActions">
                  <button
                    type="button"
                    className="link"
                    disabled={index === 0}
                    onClick={() => {
                      const next = [...stops];
                      [next[index - 1], next[index]] = [next[index], next[index - 1]];
                      setStops(next);
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="link"
                    disabled={index === stops.length - 1}
                    onClick={() => {
                      const next = [...stops];
                      [next[index], next[index + 1]] = [next[index + 1], next[index]];
                      setStops(next);
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="link link--danger"
                    onClick={() => setStops(stops.filter((_, i) => i !== index))}
                  >
                    izbaci
                  </button>
                </span>
              </li>
            ))}
          </ol>

          {free.length > 0 && (
            <label>
              Dodaj objekat
              <select
                value=""
                onChange={(e) => {
                  const chosen = free.find((l) => l.code === e.target.value);
                  if (chosen) setStops([...stops, { kind: chosen.kind, code: chosen.code }]);
                }}
              >
                <option value="">— izaberi —</option>
                {free.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </label>
          )}

          {/* Put i stanice se ne menjaju zajedno — jedno je kome se nosi roba,
              drugo kuda se vozi. */}
          <p className="note">
            Ovim se menja <strong>kome</strong> pravac nosi robu. Ako se promenio i
            put, mesta se dodaju ispod, na mapi pravca, i tamo se potvrđuju.
          </p>

          <div className="setup__actions">
            <button type="button" disabled={busy || name.trim() === ''} onClick={() => void save()}>
              Sačuvaj
            </button>
            <button type="button" className="link" onClick={() => setEditing(null)}>
              Odustani
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
